import { TripType } from '@utils/trip';

export default abstract class ItineraryPriceLoader {
    constructor({
        from,
        to,
        tripType,
        adultCount,
        roomCount,
        childCount,
        infantCount,
    }: ItineraryPriceLoaderFactory) {
        this.to = to;
        this.from = from;
        this.tripType = tripType;
        this.adultCount = adultCount;
        this.childCount = childCount;
        this.infantCount = infantCount;
        this.roomCount = roomCount;
    }

    //#region state
    currentSelectionStep: 'departure' | 'return' = 'departure';
    tripType: TripType;
    protected readonly adultCount: number;
    protected readonly childCount: number;
    protected readonly infantCount?: number;
    protected readonly roomCount?: number;
    public from: string;
    public to?: string;

    /**
     * List of itinerary prices and whether it is the minimum price or not
     *
     * @type {PriceObject}
     * @memberof ItineraryPriceLoader
     */
    private departurePrices: PriceObject[] = [];
    private returnPrices: PriceObject[] = [];

    /**
     * The maximum date which trips prices are loaded
     *
     * @type {Date | null}
     * @memberof ItineraryPriceLoader
     */

    private maxLoadedDeparture: Date | null = null;
    private maxLoadedReturn: Date | null = null;
    private maxLoadingDeparture: Date | null = null;
    private maxLoadingReturn: Date | null = null;

    /**
     * check to is loading prices
     *
     * @type {boolean}
     * @memberof ItineraryPriceLoader
     */
    isLoading = false;
    //#endregion

    setCurrentSelectionStep(val: 'departure' | 'return') {
        this.currentSelectionStep = val;
    }

    switchTripType(tripType: TripType) {
        this.tripType = tripType;
    }

    get maxLoadedDate(): Date | null {
        switch (this.currentSelectionStep) {
            case 'departure':
                return this.maxLoadedDeparture;
            case 'return':
                return this.maxLoadedReturn;
            default:
                return null;
        }
    }

    get maxLoadingDate(): Date | null {
        switch (this.currentSelectionStep) {
            case 'departure':
                return this.maxLoadingDeparture;
            case 'return':
                return this.maxLoadingReturn;
            default:
                return null;
        }
    }

    get prices(): PriceObject[] {
        let prices: PriceObject[] = [];
        switch (this.currentSelectionStep) {
            case 'departure':
                prices = this.departurePrices;
                break;
            case 'return':
                prices = this.returnPrices;
                break;
        }
        const today = new Date();
        const currentDayCount = this.maxLoadingDate
            ? dayConfig.gregory(this.maxLoadingDate).diff(today, 'day')
            : 8;
        return Array(currentDayCount)
            .fill(null)
            .map((_, i) => {
                const date = dayConfig.gregory(today).add(i, 'day');
                const eDay = prices.find(p => date.isSame(p.date, 'day'));
                if (eDay) {
                    return eDay;
                } else {
                    return {
                        date: date.format('YYYY-MM-DD'),
                        value: '-',
                    };
                }
            });
    }

    /**
     * length of day have a price
     *
     * @readonly
     * @private
     * @type {number}
     * @memberof ItineraryPriceLoader
     */
    private get pricedDaysCount(): number {
        return this.prices.filter(price => price.value && price.value !== '-')
            .length;
    }

    /**
     * absolute max price in list
     *
     * @readonly
     * @type {number}
     * @memberof ItineraryPriceLoader
     */
    get maxPrice(): number {
        return this.prices.reduce((max, price) => {
            const value = Number(price.value);
            if (value && value > max) {
                return value;
            } else {
                return max;
            }
        }, -Infinity);
    }

    /**
     * absolute min price in list
     *
     * @readonly
     * @type {number}
     * @memberof ItineraryPriceLoader
     */
    get minPrice(): number {
        return this.prices.reduce((min, price) => {
            const value = Number(price.value);
            if (value && value < min) {
                return value;
            } else {
                return min;
            }
        }, Infinity);
    }

    /**
     * count of absolute min prices
     *
     * @readonly
     * @type {number}
     * @memberof ItineraryPriceLoader
     */
    get minCount(): number {
        let samePriceCount = 0;
        this.prices.forEach(price => {
            if (this.isMinPrice(price.value)) {
                samePriceCount++;
            }
        });
        return samePriceCount;
    }

    /**
     * count of absolute max prices
     *
     * @readonly
     * @type {number}
     * @memberof ItineraryPriceLoader
     */
    get maxCount(): number {
        let samePriceCount = 0;
        this.prices.forEach(price => {
            if (this.isMaxPrice(price.value)) {
                samePriceCount++;
            }
        });
        return samePriceCount;
    }

    get labeledPrices(): LabeledPriceObject[] {
        // const tMin = Math.min(20000, 0.03 * this.minPrice);
        // const tMax = Math.max(15000, 0.02 * this.maxPrice);
        // const alpha = 0.01 * this.minPrice;
        // const beta = 0.04 * this.minPrice;
        // const from = this.maxPrice - tMax;
        // let countOfTMax = 0;
        // this.prices.forEach(price => {
        //     if (
        //         Number(price.value) <= this.maxPrice &&
        //         Number(price.value) > from
        //     ) {
        //         countOfTMax++;
        //     }
        // });

        const prices: LabeledPriceObject[] = this.prices.map(p => ({
            ...p,
            isLoading: dayConfig
                .gregory(p.date)
                .isSameOrAfter(this.maxLoadedDate ?? new Date(), 'day'),
            priceStatus: 'unknown',
        }));
        // if (this.maxPrice - this.minPrice <= alpha) {
        //     prices.forEach(price => {
        //         price.priceStatus = 'unknown';
        //     });
        // } else if (
        //     alpha < this.maxPrice - this.minPrice &&
        //     this.maxPrice - this.minPrice <= beta
        // ) {
        //     if (this.minCount >= 0.65 * this.pricedDaysCount) {
        //         prices.forEach(price => {
        //             if (this.isMaxPrice(price.value)) {
        //                 price.priceStatus = 'max';
        //             }
        //         });
        //     } else {
        //         prices.forEach(price => {
        //             if (this.isMinPrice(price.value)) {
        //                 price.priceStatus = 'min';
        //             }
        //         });
        //         if (this.maxCount >= 0.65 * this.pricedDaysCount) {
        //             prices.forEach(price => {
        //                 if (price.priceStatus === 'max') {
        //                     price.priceStatus = 'unknown';
        //                 }
        //             });
        //         } else {
        //             prices.forEach(price => {
        //                 if (this.isMaxPrice(price.value)) {
        //                     price.priceStatus = 'max';
        //                 }
        //             });
        //         }
        //     }
        // } else if (this.maxPrice - this.minPrice > beta) {
        //     if (this.minCount >= 0.65 * this.pricedDaysCount) {
        //         prices.forEach(price => {
        //             if (
        //                 Number(price.value) >= this.maxPrice - tMax &&
        //                 Number(price.value) <= this.maxPrice
        //             ) {
        //                 price.priceStatus = 'max';
        //             }
        //         });
        //     } else {
        //         prices.forEach(price => {
        //             if (
        //                 Number(price.value) >= this.minPrice &&
        //                 Number(price.value) < this.minPrice + tMin
        //             ) {
        //                 price.priceStatus = 'min';
        //             }
        //         });
        //         if (this.maxCount >= 0.65 * this.pricedDaysCount) {
        //             prices.forEach(price => {
        //                 if (price.priceStatus === 'max') {
        //                     price.priceStatus = 'unknown';
        //                 }
        //             });
        //         } else if (countOfTMax >= 0.65 * this.pricedDaysCount) {
        //             prices.forEach(price => {
        //                 if (this.isMaxPrice(price.value)) {
        //                     price.priceStatus = 'max';
        //                 }
        //             });
        //         } else {
        //             prices.forEach(price => {
        //                 if (
        //                     Number(price.value) >= this.maxPrice - tMax &&
        //                     Number(price.value) <= this.maxPrice
        //                 ) {
        //                     price.priceStatus = 'max';
        //                 }
        //             });
        //         }
        //     }
        // }
        return prices;
    }

    private isMaxPrice(price: Price): boolean {
        if (!price || price === '-') {
            return false;
        }
        const diff = this.maxPrice - Number(price);
        return diff >= 0 && diff <= 20000;
    }

    private isMinPrice(price: Price): boolean {
        if (!price || price === '-') {
            return false;
        }
        const diff = Number(price) - this.minPrice;
        return diff >= 0 && diff <= 20000;
    }

    protected abstract fetchSinglePrices(
        fromDate: Date,
        toDate: Date,
        from: string,
        to?: string
    ): Promise<PriceObject[]>;

    /**
     * fetch prices from server
     *
     * @abstract
     * @memberof ItineraryPriceLoader
     */
    async fetchPrices(until?: Date) {
        const endDate =
            until ||
            dayConfig
                .jalali()
                .startOf('day')
                .add(2, 'month')
                .startOf('month')
                .toDate();
        const loadDepartureTask = async () => {
            if (
                this.maxLoadingDeparture &&
                this.maxLoadingDeparture.getTime() >= endDate.getTime()
            ) {
                return;
            }
            this.maxLoadingDeparture = endDate;
            const lastMaxLoadedDeparture = this.maxLoadedDeparture;
            const prices = await this.fetchSinglePrices(
                lastMaxLoadedDeparture || new Date(),
                endDate,
                this.from,
                this.to
            );
            this.maxLoadedDeparture = endDate;
            this.departurePrices = [...this.departurePrices, ...prices];
        };
        const loadReturnTask = async () => {
            if (
                this.maxLoadingReturn &&
                this.maxLoadingReturn.getTime() >= endDate.getTime()
            ) {
                return;
            }
            this.maxLoadingReturn = endDate;
            const lastMaxLoadedReturn = this.maxLoadedReturn;
            const prices = await this.fetchSinglePrices(
                lastMaxLoadedReturn || new Date(),
                endDate,
                this.to!,
                this.from
            );
            this.maxLoadedReturn = endDate;
            this.returnPrices = [...this.returnPrices, ...prices];
        };
        try {
            this.isLoading = true;
            if (this.tripType === TripType.OneWay) {
                await loadDepartureTask();
            } else {
                await Promise.all([loadDepartureTask(), loadReturnTask()]);
            }
        } catch (e) {
            reportException(e);
        } finally {
            this.isLoading = false;
        }
    }

    equals(other: ItineraryPriceLoader): boolean {
        return (
            this.from === other.from &&
            this.to === other.to &&
            this.adultCount === other.adultCount &&
            this.childCount === other.childCount &&
            this.infantCount === other.infantCount &&
            this.roomCount === other.roomCount
        );
    }
}

type Price = number | '-';

export type PriceObject = {
    date: string;
    value: Price;
};

export type LabeledPriceObject = PriceObject & {
    priceStatus: 'min' | 'max' | 'unknown';
    bundle?: 'departure' | 'return';
    isLoading: boolean;
};

export interface ItineraryPriceLoaderFactory {
    tripType: TripType;
    from: string;
    to?: string;
    adultCount: number;
    childCount: number;
    infantCount?: number;
    roomCount?: number;
}
